import {
  Modifier,
  SelectionState,
  CharacterMetadata,
  EditorState,
} from 'draft-js'
import {
  insertEmptyBlock,
  insertContentBlock,
  InsertTypes,
} from '../../../story/util/draftUtils'
import { Map } from 'immutable'
import blockKeysFromSelection from './blockKeysFromSelection'
import intl from 'react-intl-universal'

export const isAtomicBlock = (block) => {
  if (block) {
    return block.getType() === 'atomic'
  }
  return false
}

export const cumulativeOffset = (el) => {
  let top = 0
  let left = 0
  if (!el || !(el instanceof HTMLElement)) {
    return { top, left }
  }
  let element = el
  do {
    top += +element.offsetTop || 0
    left += +element.offsetLeft || 0
    const parent = element.offsetParent
    if (!parent || !(parent instanceof HTMLElement)) {
      break
    }

    if (parent.getAttribute('data-block') === 'editorWrapper') {
      break
    }

    element = parent
  } while (element)
  return { top, left }
}

export class Subscriber {
  constructor() {
    this._listeners = {}
  }

  publish(event, ...args) {
    if (this._listeners[event]) {
      this._listeners[event].forEach((listener) => listener(...args))
    }
  }

  subscribe(event, fn) {
    if (!this._listeners[event]) {
      this._listeners[event] = []
    }
    this._listeners[event].push(fn)
  }

  unsubscribe(event, fn) {
    if (this._listeners[event]) {
      this._listeners[event] = this._listeners[event].filter(
        (listener) => listener !== fn
      )
    }
  }
}

export const _pluginReplyToBool = (reply) => {
  const result = ['not-handled', 'handled'].findIndex((el) => el === reply)
  if (!~result) {
    throw new Error('Plugin`s handleKeyCommand returned wrong result')
  }
  return !!result
}

export const dispatchCommand = (command, editorRef) => {
  if (command && editorRef && !editorRef.getReadOnly()) {
    const pluginMethods = editorRef.getPluginMethods()
    editorRef
      .resolvePlugins()
      .filter((plugin) => plugin.handleKeyCommand)
      .some((plugin) =>
        _pluginReplyToBool(
          plugin.handleKeyCommand(
            command,
            pluginMethods.getEditorState(),
            undefined,
            pluginMethods
          )
        )
      )
  }
}

export const smartReplaceText = (contentState, selection, text) => {
  let inlineStyle
  let entityKey

  const block = contentState.getBlockForKey(selection.getStartKey())
  const isCollapsed = selection.isCollapsed()
  const beforeOffset = selection.getStartOffset() - (isCollapsed ? 1 : 0)
  const endOffset = selection.getEndOffset() - (isCollapsed ? 0 : 1)

  // Make sure to apply same style and entitiy if the insertion is in between two chars of the same type
  if (beforeOffset >= 0 && endOffset <= block.getLength()) {
    // Check style
    const beforeInlineStyle = block.getInlineStyleAt(beforeOffset)
    if (beforeInlineStyle.equals(block.getInlineStyleAt(endOffset))) {
      inlineStyle = beforeInlineStyle
    }
    // Check entity
    const beforeEntityKey = block.getEntityAt(beforeOffset)
    if (beforeEntityKey === block.getEntityAt(endOffset)) {
      entityKey = beforeEntityKey
    }
  }

  return Modifier.replaceText(
    contentState,
    selection,
    text,
    inlineStyle,
    entityKey
  )
}

export const smartInsertBlocks = (contentState, selection, blocks) => {
  let targetBlockKey = selection.getAnchorKey()

  const blockToInsert = contentState.getBlockForKey(targetBlockKey)
  const blockLength = blockToInsert.getLength()
  // If block length is 0 then just treat it as empty
  const isEmpty = blockLength === 0 && blockToInsert.getType() !== 'atomic'

  if (!isEmpty) {
    // The blockToInsert is derived from selectionState.getAnchorKey, so use anchorOffset here.
    const offset = selection.getAnchorOffset()
    let splittedBlock = contentState
    let insertType = InsertTypes.AFTER
    if (offset === 0) {
      insertType = InsertTypes.BEFORE
    } else if (offset >= blockLength) {
      insertType = InsertTypes.AFTER
    } else if (offset !== blockLength) {
      // We only need split block if it's in the middle of text, so no extra empty block before or after
      splittedBlock = Modifier.splitBlock(contentState, selection)
    }
    const { contentState: withEmptyBlock, contentBlocks } = insertEmptyBlock(
      splittedBlock,
      selection,
      insertType
    )
    targetBlockKey = contentBlocks[0].getKey()
    contentState = withEmptyBlock
  }

  const { contentState: updatedContentState } = insertContentBlock(
    contentState,
    SelectionState.createEmpty(targetBlockKey),
    blocks,
    InsertTypes.AT
  )

  return updatedContentState
}

export const getCurrentBlock = (editorState) => {
  return editorState
    .getCurrentContent()
    .getBlockForKey(editorState.getSelection().getStartKey())
}

export const getCurrentBlockANS = (editorState) => {
  const block = getCurrentBlock(editorState)
  let data
  if (isAtomicBlock(block)) {
    const blockData = block.getData()
    if (blockData) {
      data = blockData.get('ans')
    }
  }
  return data || {}
}

export const getCurrentBlockType = (editorState) => {
  const block = getCurrentBlock(editorState)
  let type = ''
  if (block) {
    type = block.getType()
    if (type === 'atomic') {
      type = getCurrentBlockANS(editorState).type
    }
  }

  return type
}

// forceRedrawBlock force draftjs to redraw specified block. This will revalidate all decoratos
// It's helpful when non-current block have to be updated due to decorator condition change
export const forceRedrawBlock = (contentState, blockKey) => {
  const block = contentState.getBlockForKey(blockKey)
  if (!block) {
    return contentState
  }
  // Have to create new immutable instance of contentBlock which makes Draft to rebuild
  // tree and call decorators again effectively clearing any decorated text
  const blockData = block.getData() || Map()
  return Modifier.setBlockData(
    contentState,
    SelectionState.createEmpty(blockKey),
    blockData.set('dummy', +Date.now())
  )
}

// Remove style types, returned by FilterStylesCallback
// Go over characters in the block, call FilterStylesCallback for each symbol and remove
// all returned styles withing that block
export const removeStyles = (block, filterStylesCallback, start = 0, end) => {
  const characterList = block.getCharacterList()
  end || (end = characterList.size)
  const newCharacterList = characterList.map((characterMetadata, charIndex) => {
    if (charIndex < start || charIndex >= end) {
      return characterMetadata
    }
    const stylesToRemove = filterStylesCallback(characterMetadata, charIndex)
    return stylesToRemove.reduce(
      (cm, style) => CharacterMetadata.removeStyle(cm, style),
      characterMetadata
    )
  })

  return block.set('characterList', newCharacterList)
}

const applyStyleDefaultChecker = (characterMetadata, styleType) =>
  !characterMetadata.hasStyle(styleType)

// Apply styleType to the specified block
export const applyStyle = (block, styleType, start = 0, end, checker) => {
  const checkerCallback = checker || applyStyleDefaultChecker
  const characterList = block.getCharacterList()
  end || (end = characterList.size)
  const newCharacterList = characterList.map((characterMetadata, charIndex) =>
    charIndex >= start &&
    charIndex < end &&
    checkerCallback(characterMetadata, styleType, charIndex)
      ? CharacterMetadata.applyStyle(characterMetadata, styleType)
      : characterMetadata
  )

  return block.set('characterList', newCharacterList)
}

// Update existing ContentBlock in a ContentState
export const updateBlock = (contentState, block) => {
  return contentState.set(
    'blockMap',
    contentState.getBlockMap().set(block.getKey(), block)
  )
}

// Collapse selection either to the begin of the selection or to the end of the selecton
export const collapseSelection = (selection) => {
  const key = selection.getEndKey()
  const offset = selection.getEndOffset()
  return selection.merge({
    anchorKey: key,
    anchorOffset: offset,
    focusKey: key,
    focusOffset: offset,
  })
}

// If selection is cross multiple blocks, shrink the range to the anchor block only
export const limitSelectionInOneBlock = (editorState) => {
  const selection = editorState.getSelection()
  if (
    !selection.isCollapsed() &&
    selection.getAnchorKey() !== selection.getFocusKey()
  ) {
    const anchorKey = selection.getAnchorKey()
    return selection.merge({
      focusKey: anchorKey,
      focusOffset: selection.getIsBackward()
        ? 0
        : editorState.getCurrentContent().getBlockForKey(anchorKey).getLength(),
    })
  }

  return selection
}

export const iterateOverNonAtomicSelection = (
  contentState,
  selectionState,
  iterateCallback
) => {
  const blockKeys = blockKeysFromSelection(contentState, selectionState)
  return blockKeys.reduce((cs, blockKey) => {
    const block = cs.getBlockForKey(blockKey)
    if (isAtomicBlock(block)) {
      return cs
    }
    const start =
      selectionState.getStartKey() === blockKey
        ? Number(selectionState.getStartOffset())
        : 0
    const end =
      selectionState.getEndKey() === blockKey
        ? Number(selectionState.getEndOffset())
        : block.getText().length
    return updateBlock(cs, iterateCallback(block, start, end))
  }, contentState)
}

export const getCurrentLength = (config, editorState) => {
  if (Object.prototype.hasOwnProperty.call(config, 'maxLength')) {
    return editorState.getCurrentContent().getPlainText('').length
  }
}

export const replaceText = (editorState, text) => {
  const contentState = editorState.getCurrentContent()
  const rangeToReplace = editorState.getSelection()
  const replace = Modifier.replaceText(contentState, rangeToReplace, text)
  editorState = EditorState.push(editorState, replace, 'replace-text')
  return editorState
}

export const getUserLocale = () =>
  intl.determineLocale(intl.getInitOptions()).slice(0, 2) || ''
